/**
 * Session state. Genuine client state, so it belongs in a Redux slice — unlike anything the
 * server owns, which belongs in RTK Query.
 *
 * What is *not* here: the access token (memory-only module, see `tokenStore.ts`) and the refresh
 * token (an HttpOnly cookie this code cannot read, by design).
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CurrentUser, ShopId, Session } from '../types/api.js';
import { clearAccessToken, setAccessToken } from './tokenStore.js';

/**
 * `locked` is the state that makes PIN unlock possible: the refresh cookie is presumed present
 * and the screen asks for six digits instead of a password. The presumption is checked by the
 * first request, and a wrong guess costs one round trip and lands on the password screen.
 *
 * `paused` is the dashboard's equivalent for a device with no PIN: the refresh cookie is
 * presumed present and a "Continue as {name}?" screen asks for one click instead of six digits
 * or a password. Never set by the POS — it keeps `locked`, on purpose, because that click is
 * strictly weaker than a PIN. See `ContinueSessionCommand` on the server.
 */
export type SessionStatus = 'restoring' | 'anonymous' | 'locked' | 'paused' | 'active' | 'revoked';

/**
 * A non-sensitive hint in `localStorage` so a cold start knows whether to show the PIN keypad or
 * the password form. It grants nothing — losing it costs a password login, and forging it gets
 * you a 401.
 */
export type SessionHint = {
  readonly userId: string;
  readonly username: string;
  readonly fullName: string;
  readonly hasPin: boolean;
  /**
   * The look and name of the shop this device last signed into, so the sign-in screen can wear
   * them before anybody is identified. **Display data only.** Never sent to the server, never
   * read by anything that decides who a person is, and no more sensitive than the shop's sign
   * over the door. Absent on a device that has never signed in — that is the neutral state.
   */
  readonly shopThemeCode?: string;
  readonly shopName?: string;
};

const HINT_KEY = 'pos.session.hint';

export function readHint(): SessionHint | null {
  try {
    const raw = localStorage.getItem(HINT_KEY);
    return raw === null ? null : (JSON.parse(raw) as SessionHint);
  } catch {
    return null;
  }
}

function writeHint(hint: SessionHint | null): void {
  try {
    if (hint === null) localStorage.removeItem(HINT_KEY);
    else localStorage.setItem(HINT_KEY, JSON.stringify(hint));
  } catch {
    /* storage disabled; the app falls back to the password screen every time */
  }
}

export type SessionState = {
  readonly status: SessionStatus;
  readonly user: CurrentUser | null;
  /** The shop being sold from. Defaults to the user's home shop, switchable where permitted. */
  readonly activeShopId: ShopId | null;
  readonly hint: SessionHint | null;
  /** Shown on the revoked screen. Explains why they were signed out — never a bare "signed out". */
  readonly revokedReason: string | null;
  /** Where the user was heading before the guard sent them to sign in. */
  readonly intendedPath: string | null;
};

const initialState: SessionState = {
  status: 'restoring',
  user: null,
  activeShopId: null,
  hint: readHint(),
  revokedReason: null,
  intendedPath: null,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** Boot resolved: no session to restore, or a locked one waiting for a PIN. */
    /** `status` is the boot outcome once the app has looked at its own hint — see `SessionStatus`. */
    restored(state, action: PayloadAction<{ status: 'locked' | 'paused' | 'anonymous' }>) {
      state.status = action.payload.status;
    },

    signedIn(state, action: PayloadAction<Session>) {
      const { user, accessToken, accessTokenExpiresAt } = action.payload;

      setAccessToken(accessToken, accessTokenExpiresAt);

      state.status = 'active';
      state.user = user;
      state.activeShopId = user.homeShopId;
      state.revokedReason = null;
      state.hint = {
        userId: user.id,
        username: user.username,
        fullName: user.fullName,
        hasPin: user.hasPin,
        shopThemeCode: user.homeShopTheme,
        shopName: user.homeShopName,
      };

      writeHint(state.hint);
    },

    /** The access token was refreshed without the user doing anything. */
    renewed(state, action: PayloadAction<Session>) {
      setAccessToken(action.payload.accessToken, action.payload.accessTokenExpiresAt);
      state.user = action.payload.user;
    },

    activeShopChanged(state, action: PayloadAction<ShopId>) {
      state.activeShopId = action.payload;
    },

    /**
     * Fast user switching. The hint goes, so the next screen is a password form, but the
     * catalogue cache stays — re-downloading 3,000 variants to change who is standing at the
     * till is the friction the cache exists to remove.
     */
    switchedUser(state) {
      clearAccessToken();
      state.status = 'anonymous';
      state.user = null;
      state.activeShopId = null;
      state.hint = null;
      state.intendedPath = null;
      writeHint(null);
    },

    signedOut(state) {
      clearAccessToken();
      state.status = 'anonymous';
      state.user = null;
      state.activeShopId = null;
      state.hint = null;
      state.revokedReason = null;
      state.intendedPath = null;
      writeHint(null);
    },

    /**
     * POS only: the access token expired while this tab was away — a backgrounded tab woken up,
     * not a revocation (see `baseQuery.ts`'s `recoverSession`). Unlike `revoked`, nothing local is
     * wiped: the hint, the active shop and the cached catalogue all survive, since the PIN is the
     * only thing actually missing. Never dispatched for the dashboard — it recovers this same
     * case silently via `/auth/continue` and only reaches `revoked` if that also fails.
     */
    expiredLocally(state) {
      clearAccessToken();
      state.status = 'locked';
    },

    /**
     * `DEVICE_REVOKED`, or five failed PIN attempts. The caller wipes local state; this records
     * why, so the screen can say what happened instead of dumping the user at a login form.
     */
    revoked(state, action: PayloadAction<string>) {
      clearAccessToken();
      state.status = 'revoked';
      state.user = null;
      state.activeShopId = null;
      state.hint = null;
      state.revokedReason = action.payload;
      writeHint(null);
    },

    intendedPathSet(state, action: PayloadAction<string | null>) {
      state.intendedPath = action.payload;
    },

    /**
     * `POST /auth/me/pin` returns nothing to sign back in with — the session it changes is the
     * one already active — so the new `hasPin` has to be applied here rather than arriving on a
     * fresh `Session`. Kept in step with the hint too, since that is what a cold start reads to
     * decide between the PIN keypad and the password form.
     */
    /** The tour was finished or skipped: it must not start again on the next page in this session. */
    tourSeen(state, action: PayloadAction<'dashboard' | 'pos'>) {
      if (!state.user) return;
      state.user =
        action.payload === 'dashboard'
          ? { ...state.user, dashboardTourSeen: true }
          : { ...state.user, posTourSeen: true };
    },

    hasPinChanged(state, action: PayloadAction<boolean>) {
      if (state.user) state.user = { ...state.user, hasPin: action.payload };

      if (state.hint) {
        state.hint = { ...state.hint, hasPin: action.payload };
        writeHint(state.hint);
      }
    },
  },
});

export const {
  restored,
  signedIn,
  renewed,
  activeShopChanged,
  switchedUser,
  signedOut,
  expiredLocally,
  revoked,
  intendedPathSet,
  hasPinChanged,
  tourSeen,
} = sessionSlice.actions;

export const sessionReducer = sessionSlice.reducer;

// ── Selectors ───────────────────────────────────────────────────────────────
// Structural parameter type so both apps' RootState satisfy it without this package importing
// either store — which would be a dependency pointing the wrong way.
type WithSession = { readonly session: SessionState };

export const selectSession = (state: WithSession): SessionState => state.session;
export const selectUser = (state: WithSession): CurrentUser | null => state.session.user;
export const selectStatus = (state: WithSession): SessionStatus => state.session.status;
export const selectIsAuthenticated = (state: WithSession): boolean => state.session.status === 'active';
export const selectActiveShopId = (state: WithSession): ShopId | null => state.session.activeShopId;
export const selectPermissions = (state: WithSession): readonly string[] =>
  state.session.user?.permissions ?? [];
